import React, { useCallback, useEffect, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import DailyTask from 'components/Widgets/DailyWidget/DailyTask';
import PersonnelWidget from 'components/Widgets/PersonnelWidget/PersonnelWidget';
import NewsFeed from 'components/Widgets/NewsFeed/NewsFeed';
import LayoutWithMap from 'containers/Base/LayoutWithMap';
import API from 'services/api';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useDispatch, useSelector } from 'react-redux';
import { getTimezone } from 'reducers/utils';
import {
  endOfWeek,
  isSameAsToday,
  isTodayinRange,
  startOfWeek,
  startOfDay,
  endOfDay,
} from 'utils/time';
import { TASK_TYPE } from 'utils/constants/task';
import { hasService } from 'utils/helper/product';
import { serviceTypes } from 'utils/constants/serviceTypes';
import {
  getCompanyAssetCategories,
  getSecurityCompanyRoles,
  getSecurityCompanyServices,
} from 'reducers/securityCompany';
import isEmpty from 'lodash/isEmpty';
import Calendar from 'components/Widgets/CalendarWidget/calendarWidget';
import cn from 'classnames';
import { WIDGET } from 'utils/constants/css';
import {
  transformAssetDashboard,
  transformEventDashboard,
  transformPersonnelDashboard,
} from 'utils/DataTransform/dashboardTransform';
import {
  BASE_INCIDENT_STATUS,
  EVENT_INCIDENT_STATUS,
} from 'utils/constants/eventStatus';
import { getUserId } from 'reducers/users';
import {
  personnelSelectors,
  personnelGridActions,
} from 'containers/Pages/People/Personnel';
import {
  assetSelectors,
  assetGridActions,
} from 'containers/Pages/Assets/Assets';
import {
  cleaningDataGridActions,
  cleaningTaskSelectors,
} from 'containers/Pages/Tasks/CleanerTasks';
import {
  workOrderGridTableActions,
  workOrderSelectors,
} from 'containers/Pages/Tasks/WorkOrders';
import {
  securityGridTableActions,
  securitySelectors,
} from 'containers/Pages/Tasks/Security';
import {
  patrolTemplateGridActions,
  patrolTemplateSelectors,
} from 'containers/Pages/Tasks/PatrolTemplates';
import { tripSelectors, tripActions } from 'containers/Pages/Tasks/Trips';
import {
  visitorActions,
  visitorSelectors,
} from 'containers/Pages/Locations/Visitors';
import { eventSelectors, eventsGridActions } from 'containers/Pages/Events';

dayjs.extend(utc);

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(1),
  },
  paper: {
    padding: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.primary,
    background: theme.palette.background.default,
  },
  Calendar: {
    height: WIDGET.Calendar.height,
    position: 'relative',
  },
  NewsFeed: {
    height: WIDGET.NEWSFEED.height,
  },
}));

const filterTodayOnlyTasks = taskItems => {
  if (isEmpty(taskItems)) return [];
  return taskItems.filter(f => {
    return isSameAsToday({ date: f.date, compareParam: 'date' });
  });
};

const Dashboard = () => {
  const services = useSelector(state => getSecurityCompanyServices(state));
  const personnelRefreshKey = useSelector(state =>
    personnelSelectors.getForceRefresh(state),
  );
  const assetRefreshKey = useSelector(state =>
    assetSelectors.getForceRefresh(state),
  );
  const cleaningRefreshKey = useSelector(state =>
    cleaningTaskSelectors.getForceRefresh(state),
  );
  const workOrderRefreshKey = useSelector(state =>
    workOrderSelectors.getForceRefresh(state),
  );
  const securityRefreshKey = useSelector(state =>
    securitySelectors.getForceRefresh(state),
  );
  const patrolTemplateRefreshKey = useSelector(state =>
    patrolTemplateSelectors.getForceRefresh(state),
  );
  const tripRefreshKey = useSelector(state =>
    tripSelectors.getForceRefresh(state),
  );
  const visitRefreshKey = useSelector(state =>
    visitorSelectors.getForceRefresh(state),
  );
  const eventsRefreshKey = useSelector(state =>
    eventSelectors.getForceRefresh(state),
  );
  const dispatch = useDispatch();
  const roles = useSelector(state => getSecurityCompanyRoles(state));
  const categories = useSelector(state => getCompanyAssetCategories(state));
  const user = useSelector(state => getUserId(state));
  const classes = useStyles();
  const tz = useSelector(state => getTimezone(state));
  // ------------------------------------------------------------------------------------------------
  const [toFetchDate, setToFetchDate] = useState();
  const todayRef = useRef(false);
  const [dateToday, setDateToday] = useState();
  const [loading, setLoading] = useState(false);

  //------------------------------------------------------------------------
  const [dashboardData, setDashboardData] = useState({
    //--------calendar data------------
    cleaningTaskItems: {},
    workOrderTaskItems: {},
    securityTaskItems: {},
    patrolTaskItems: {},
    tripTaskItems: {},
    //----------Widget data-------------------------------------
    widgetData: {
      assets: [],
      personnel: [],
      cleaning: [],
      workOrders: [],
      security: [],
      patrols: [],
      visits: [],
      transportTask: [],
      events: [],
    },
  });
  const startDate = startOfWeek({
    //1
    date: toFetchDate || new Date(),
  });
  const endDate = endOfWeek({
    //5
    date: toFetchDate || new Date(),
  });
  // personnelDashboard api call
  const fetchPersonnelDashboardItems = async () => {
    try {
      const {
        data: personnelDashItems,
      } = await API.fetchPersonnelDashboardItems({});
      if (personnelDashItems.data) {
        dispatch(personnelGridActions.setForceRefresh(false));
        return transformPersonnelDashboard({
          personnelList: personnelDashItems.data,
          roles,
        });
      }
    } catch (error) {
      dispatch(personnelGridActions.setForceRefresh(false));
      console.error('error: ', error);
    }
    return [];
  };

  // asset dashboard api call
  const fetchAssetDashboardItems = async () => {
    try {
      const { data: assetDashItems } = await API.fetchAssetDashboardItems({
        categories,
      });
      if (assetDashItems.data) {
        dispatch(assetGridActions.setForceRefresh(false));
        return transformAssetDashboard({
          assetList: assetDashItems.data,
          categories: categories,
        });
      }
    } catch (error) {
      dispatch(assetGridActions.setForceRefresh(false));
      console.log('error: ', error);
    }
    return [];
  };
  // visits task api call
  const fetchVisitsDashboardItems = async ({ startDate, endDate }) => {
    try {
      const { data } = await API.fetchVisitDashboardItems({
        fromDate: startDate,
        toDate: endDate,
        timezone: tz,
      });
      if (data.data) {
        dispatch(visitorActions.setForceRefresh(false));
        return data.data;
      }
    } catch (error) {
      dispatch(visitorActions.setForceRefresh(false));
      console.log('error: ', error);
    }
    return [];
  };

  const fetchEventsDashboardItems = async () => {
    try {
      const { data: eventDashItems } = await API.fetchEventList({
        incidentStatus: [...EVENT_INCIDENT_STATUS].filter(
          f => f.id !== BASE_INCIDENT_STATUS.COMPLETED_EVENT.key,
        ),
        assigneeId: user,
        creatorId: user,
        includeArchive: false,
        includeUnAssigned: true,
      });
      if (eventDashItems.data) {
        dispatch(eventsGridActions.setForceRefresh(false));
        return transformEventDashboard({
          eventList: eventDashItems.data,
          timeZone: tz,
        });
      }
    } catch (error) {
      dispatch(eventsGridActions.setForceRefresh(false));
      console.log('error: ', error);
    }
    return [];
  };
  // task api call template for cleaning, workorders, security, patrolTasks and trips
  const fetchTaskDashboardItems = async ({
    startDate,
    endDate,
    refreshKey,
    taskType,
    action,
    taskApi,
  }) => {
    try {
      const todayIncluded = isTodayinRange({
        startDate,
        endDate,
      });
      if (refreshKey && !todayIncluded) {
        const { data: fiveDayTasks } = await taskApi({
          fromDate: startDate,
          toDate: endDate,
          timezone: tz,
          ...(taskType && { taskCategory: [taskType] }),
        });
        const sod = startOfDay({
          date: new Date(), // start of today
        });
        const eod = endOfDay({
          date: new Date(), // end of today
        });
        const { data: todaysTasks } = await taskApi({
          fromDate: sod,
          toDate: eod,
          timezone: tz,
          ...(taskApi && { taskCategory: [taskType] }),
        });
        if (!isEmpty(todaysTasks) && !isEmpty(fiveDayTasks)) {
          dispatch(action);
          return {
            fiveDayTasks: fiveDayTasks.data,
            todaysTasks: filterTodayOnlyTasks(todaysTasks.data?.dashboardItems),
          };
        }
      }

      const { data } = await taskApi({
        fromDate: startDate,
        toDate: endDate,
        timezone: tz,
        ...(taskType && { taskCategory: [taskType] }),
      });
      if (data.data) {
        dispatch(action);
        return {
          fiveDayTasks: data.data,
          todaysTasks: filterTodayOnlyTasks(data.data?.dashboardItems),
        };
      }
    } catch (error) {
      dispatch(action);
      console.log('error: ', error);
    }
    return [];
  };

  // Calendar based api calls
  const fetchCalendarItems = useCallback(
    async services => {
      //Add the api calls to be made here for calendar
      let cleaningTaskItems = '';
      let workOrderTaskItems = '';
      let securityTaskItems = '';
      let patrolTaskItems = '';
      let tripTaskItems = '';
      try {
        setLoading(true);
        //--------cleaning fetchTaskDashboardItems items-----------
        if (
          hasService({
            services,
            service: serviceTypes.cleaning,
          })
        ) {
          cleaningTaskItems = await fetchTaskDashboardItems({
            startDate,
            endDate,
            taskType: TASK_TYPE.CLEANING.id,
            action: cleaningDataGridActions.setForceRefresh(false),
            taskApi: API.fetchTaskDashboardItems,
          });
        }
        //------------------------Workorders------------------------------------------
        if (
          hasService({
            services,
            service: serviceTypes.maintenance,
          })
        ) {
          workOrderTaskItems = await fetchTaskDashboardItems({
            startDate,
            endDate,
            taskType: TASK_TYPE.MAINTENANCE.id,
            action: workOrderGridTableActions.setForceRefresh(false),
            taskApi: API.fetchTaskDashboardItems,
          });
        }
        //---------------------Security task----------------------------------
        if (
          hasService({
            services,
            service: serviceTypes.security,
          })
        ) {
          securityTaskItems = await fetchTaskDashboardItems({
            startDate,
            endDate,
            taskType: TASK_TYPE.SECURITY.id,
            action: securityGridTableActions.setForceRefresh(false),
            taskApi: API.fetchTaskDashboardItems,
          });
        }
        //-----------------------patrol-------------------------------------------
        if (
          hasService({
            services,
            service: serviceTypes.patrol.singleSite,
          }) ||
          hasService({
            services,
            service: serviceTypes.patrol.multiSite,
          })
        ) {
          patrolTaskItems = await fetchTaskDashboardItems({
            startDate,
            endDate,
            taskApi: API.fetchPatrolDashboardItems,
            action: patrolTemplateGridActions.setForceRefresh(false),
          });
        }
        //--------------------------trips----------------------------------------
        if (
          hasService({
            services,
            service: serviceTypes.transport.standard,
          })
        ) {
          tripTaskItems = await fetchTaskDashboardItems({
            startDate,
            endDate,
            taskApi: API.fetchTripDashboardItems,
            action: tripActions.setForceRefresh(false),
          });
        }
        //------------------------------------------------------------------

        //--set the data into the state after all the api calls are done
        setDashboardData(prev => ({
          ...prev,
          cleaningTaskItems: cleaningTaskItems?.fiveDayTasks,
          workOrderTaskItems: workOrderTaskItems?.fiveDayTasks,
          securityTaskItems: securityTaskItems?.fiveDayTasks,
          patrolTaskItems: patrolTaskItems?.fiveDayTasks,
          tripTaskItems: tripTaskItems?.fiveDayTasks,
          ...(!todayRef.current && {
            widgetData: {
              ...prev.widgetData,
              cleaning: cleaningTaskItems?.todaysTasks,
              workOrders: workOrderTaskItems?.todaysTasks,
              security: securityTaskItems?.todaysTasks,
              patrols: patrolTaskItems?.todaysTasks,
              transportTask: tripTaskItems?.todaysTasks,
            },
          }),
        }));
        //------------------------------------------------------------------
        todayRef.current = true;
        setDateToday(toFetchDate);
        setLoading(false);
      } catch (err) {
        todayRef.current = false;
        setLoading(false);
        console.log(err);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tz, toFetchDate],
  );

  const fetchWidgetData = async () => {
    let personnelItems = '';
    let visitItems = '';
    let assetItems = '';
    let eventItems = '';
    if (
      hasService({
        services,
        service: serviceTypes.visitorManagement,
      }) &&
      !todayRef.current
    ) {
      //visits will not show up on the 5-day view only today.
      visitItems = await fetchVisitsDashboardItems({
        startDate,
        endDate,
      });
    }
    //-----personnel Items---------------
    personnelItems = await fetchPersonnelDashboardItems();

    //-----asset Items---------------
    assetItems = await fetchAssetDashboardItems();

    //--------eventitems-------
    eventItems = await fetchEventsDashboardItems();

    setDashboardData(prev => ({
      ...prev,
      widgetData: {
        ...prev.widgetData,
        personnel: personnelItems,
        assets: assetItems,
        visits: filterTodayOnlyTasks(visitItems?.dashboardItems),
        events: eventItems,
      },
    }));
  };

  useEffect(() => {
    if (!isEmpty(services)) {
      fetchCalendarItems(services, toFetchDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services, roles, user, categories, toFetchDate]);

  useEffect(() => {
    fetchWidgetData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // fetch the data on the first render only

  // this will only run when personnel list is changed
  useEffect(() => {
    const fetchPersonnel = async () => {
      const items = await fetchPersonnelDashboardItems();

      setDashboardData(prev => ({
        ...prev,
        widgetData: {
          ...prev.widgetData,
          personnel: items,
        },
      }));
    };
    if (personnelRefreshKey) {
      fetchPersonnel();
    }
    // only render on personnel refresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personnelRefreshKey]);

  // refresh on asset
  useEffect(() => {
    const fetchAssets = async () => {
      const items = await fetchAssetDashboardItems();
      setDashboardData(prev => ({
        ...prev,
        widgetData: {
          ...prev.widgetData,
          assets: items,
        },
      }));
    };
    if (assetRefreshKey) {
      fetchAssets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetRefreshKey]);

  // refresh on visit
  useEffect(() => {
    const fetchVisits = async () => {
      const items = await fetchVisitsDashboardItems();
      setDashboardData(prev => ({
        ...prev,
        widgetData: {
          ...prev.widgetData,
          visits: items,
        },
      }));
    };
    if (visitRefreshKey) {
      fetchVisits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitRefreshKey]);

  // refresh on events
  useEffect(() => {
    const fetchEvents = async () => {
      const items = await fetchEventsDashboardItems();
      setDashboardData(prev => ({
        ...prev,
        widgetData: {
          ...prev.widgetData,
          events: items,
        },
      }));
    };
    if (eventsRefreshKey) {
      fetchEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsRefreshKey]);

  // refresh cleaning tasks
  useEffect(() => {
    const fetchCleaningTasks = async () => {
      const { fiveDayTasks, todaysTasks } = await fetchTaskDashboardItems({
        startDate,
        endDate,
        refreshKey: cleaningRefreshKey,
        taskType: TASK_TYPE.CLEANING.id,
        action: cleaningDataGridActions.setForceRefresh(false),
        taskApi: API.fetchTaskDashboardItems,
      });

      setDashboardData(prev => ({
        ...prev,
        cleaningTaskItems: fiveDayTasks,
        widgetData: {
          ...prev.widgetData,
          cleaning: todaysTasks,
        },
      }));
    };
    if (cleaningRefreshKey) {
      fetchCleaningTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleaningRefreshKey]);

  // refresh workorder tasks
  useEffect(() => {
    const fetchWorkOrderTasks = async () => {
      const { fiveDayTasks, todaysTasks } = await fetchTaskDashboardItems({
        startDate,
        endDate,
        refreshKey: workOrderRefreshKey,
        taskType: TASK_TYPE.MAINTENANCE.id,
        action: workOrderGridTableActions.setForceRefresh(false),
        taskApi: API.fetchTaskDashboardItems,
      });

      setDashboardData(prev => ({
        ...prev,
        workOrderTaskItems: fiveDayTasks,
        widgetData: {
          ...prev.widgetData,
          workOrders: todaysTasks,
        },
      }));
    };
    if (workOrderRefreshKey) {
      fetchWorkOrderTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrderRefreshKey]);

  // refresh security tasks
  useEffect(() => {
    const fetchSecurityTasks = async () => {
      const { fiveDayTasks, todaysTasks } = await fetchTaskDashboardItems({
        startDate,
        endDate,
        refreshKey: securityRefreshKey,
        taskType: TASK_TYPE.SECURITY.id,
        action: securityGridTableActions.setForceRefresh(false),
        taskApi: API.fetchTaskDashboardItems,
      });

      setDashboardData(prev => ({
        ...prev,
        securityTaskItems: fiveDayTasks,
        widgetData: {
          ...prev.widgetData,
          security: todaysTasks,
        },
      }));
    };
    if (securityRefreshKey) {
      fetchSecurityTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [securityRefreshKey]);

  // refresh trip tasks
  useEffect(() => {
    const fetchTrips = async () => {
      const { fiveDayTasks, todaysTasks } = await fetchTaskDashboardItems({
        startDate,
        endDate,
        refreshKey: tripRefreshKey,
        taskApi: API.fetchTripDashboardItems,
        action: tripActions.setForceRefresh(false),
      });

      setDashboardData(prev => ({
        ...prev,
        tripTaskItems: fiveDayTasks,
        widgetData: {
          ...prev.widgetData,
          transportTask: todaysTasks,
        },
      }));
    };
    if (tripRefreshKey) {
      fetchTrips();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripRefreshKey]);

  // refresh patrol tasks
  useEffect(() => {
    const fetchPatrolTasks = async () => {
      const { fiveDayTasks, todaysTasks } = await fetchTaskDashboardItems({
        startDate,
        endDate,
        refreshKey: patrolTemplateRefreshKey,
        taskApi: API.fetchPatrolDashboardItems,
        action: patrolTemplateGridActions.setForceRefresh(false),
      });

      setDashboardData(prev => ({
        ...prev,
        patrolTaskItems: fiveDayTasks,
        widgetData: {
          ...prev.widgetData,
          patrols: todaysTasks,
        },
      }));
    };
    if (patrolTemplateRefreshKey) {
      fetchPatrolTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patrolTemplateRefreshKey]);

  const onRangeChange = (date = []) => {
    const [firstDate] = date;
    setToFetchDate(firstDate);
  };

  const DashboardComponent = () => (
    <div className={classes.root}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3} sm={12}>
          <Paper className={classes.paper}>
            <PersonnelWidget
              data={dashboardData.widgetData.personnel}
              title={'Personnel'}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={3} sm={12}>
          <Paper className={classes.paper}>
            <PersonnelWidget
              data={dashboardData.widgetData.assets}
              title={'Assets'}
            />
          </Paper>
        </Grid>
        {hasService({
          services,
          service: serviceTypes.cleaning,
        }) && (
          <Grid item xs={6} md={3} sm={6}>
            <Paper className={classes.paper}>
              <DailyTask
                title={'Cleaning Tasks'}
                shouldTranslate={false}
                taskData={dashboardData.widgetData.cleaning}
              />
            </Paper>
          </Grid>
        )}
        {hasService({
          services,
          service: serviceTypes.maintenance,
        }) && (
          <Grid item xs={6} md={3} sm={6}>
            <Paper className={classes.paper}>
              <DailyTask
                title={'Workorder Tasks'}
                shouldTranslate={false}
                taskData={dashboardData.widgetData.workOrders}
              />
            </Paper>
          </Grid>
        )}
        {hasService({
          services,
          service: serviceTypes.security,
        }) && (
          <Grid item xs={6} md={3} sm={6}>
            <Paper className={classes.paper}>
              <DailyTask
                title={'Security Tasks'}
                shouldTranslate={false}
                taskData={dashboardData.widgetData.security}
              />
            </Paper>
          </Grid>
        )}
        {(hasService({
          services,
          service: serviceTypes.patrol.singleSite,
        }) ||
          hasService({
            services,
            service: serviceTypes.patrol.multiSite,
          })) && (
          <Grid item xs={6} md={3} sm={6}>
            <Paper className={classes.paper}>
              <DailyTask
                title={'Patrol Tasks'}
                taskData={dashboardData.widgetData.patrols}
              />
            </Paper>
          </Grid>
        )}
        {hasService({
          services,
          service: serviceTypes.visitorManagement,
        }) && (
          <Grid item xs={6} md={3} sm={6}>
            <Paper className={classes.paper}>
              <DailyTask
                title={'Visitors'}
                shouldTranslate={false}
                blankItemLabel={'No sites available'}
                taskData={dashboardData.widgetData.visits}
              />
            </Paper>
          </Grid>
        )}
        {hasService({
          services,
          service: serviceTypes.transport.standard,
        }) && (
          <Grid item xs={6} md={3} sm={6}>
            <Paper className={classes.paper}>
              <DailyTask
                title={'Transport Tasks'}
                taskData={dashboardData.widgetData.transportTask}
              />
            </Paper>
          </Grid>
        )}
      </Grid>
      <br />
      <Grid container spacing={2}>
        <Grid item xs={12} md={4} sm={12}>
          <Paper className={cn(classes.paper, classes.NewsFeed)}>
            <NewsFeed data={dashboardData.widgetData.events} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={8} sm={12}>
          <Paper className={cn(classes.paper, classes.Calendar)}>
            <Calendar
              cleaningTaskItems={dashboardData.cleaningTaskItems}
              workOrderTaskItems={dashboardData.workOrderTaskItems}
              patrolTaskItems={dashboardData.patrolTaskItems}
              securityTaskItems={dashboardData.securityTaskItems}
              tripTaskItems={dashboardData.tripTaskItems}
              onRangeChange={onRangeChange}
              date={dateToday}
              loaded={loading}
            />
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
  return (
    <LayoutWithMap
      DashboardComponent={DashboardComponent}
      showListView={false}
      filtersRequired={false}
    />
  );
};

export default Dashboard;
